import {
  formatSecondsToTlim,
  getRespiratoryEnduranceModalityLabel,
  getRespiratoryEnduranceReferenceLabel,
  summarizePemaxPayload,
  summarizePimaxPayload,
  getPimaxPrimarySeries,
  getPimaxPositionSeries,
  pimaxSeriesAreComparable,
  type PimaxPositionSeries,
  summarizeRespiratoryEndurancePayload,
  summarizeSIndexPayload,
  type PemaxResultPayload,
  type PimaxResultPayload,
  type RespiratoryEnduranceProtocol,
  type RespiratoryEnduranceResultPayload,
  type SIndexResultPayload,
} from '../respiratory-muscle/respiratoryMuscleDomain';
import { summarizeGaitSpeedResult, type GaitSpeedResultPayload } from '../gait-speed/gaitSpeedDomain';
import { summarizePeakFlowPayload, type PeakFlowResultPayload } from '../peak-flow/peakFlowDomain';
import { summarizeStsResult, type StsTestKey } from '../sts/stsDomain';

export type AssessmentHistoryComparisonTestKey =
  | 'PIMAX'
  | 'PEMAX'
  | 'S_INDEX'
  | 'RESP_ENDURANCE'
  | 'PEAK_FLOW'
  | '4MGS'
  | '5xSTS'
  | 'STS_30S'
  | 'STS_1MIN';

export interface AssessmentHistoryComparisonEntry {
  id: string;
  assessmentDate?: string | null;
  payload: unknown;
}

interface AssessmentHistoryComparisonCardProps {
  testKey: AssessmentHistoryComparisonTestKey;
  currentPayload: unknown;
  currentAssessmentDate?: string | null;
  history?: AssessmentHistoryComparisonEntry[];
}

type ComparisonSnapshot = {
  pimaxSeries?: PimaxPositionSeries | null;
  primaryValue: number | null;
  primaryFormatted: string;
  deltaKind: 'pressure' | 'duration' | 'peak-flow' | 'gait-speed' | 'sts-time' | 'repetitions';
  lowerIsBetter: boolean;
  stableTolerance: number;
  percentPredicted: number | null;
  sustained1s: number | null;
  referenceKey: string | null;
  protocol: RespiratoryEnduranceProtocol | null;
  protocolLabel: string | null;
  chairHeightCm: number | null;
  usedArms: boolean | null;
  walkingAidLabel: string | null;
  physicalAssistance: boolean | null;
  distanceMeters: number | null;
};

type Compatibility = {
  comparable: boolean;
  note?: string;
};

const formatNumber = (value: number, decimals: number): string =>
  value.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const formatAssessmentDate = (value?: string | null): string => {
  if (!value) return 'Data não informada';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Data não informada';
  return date.toLocaleDateString('pt-BR');
};

const formatSignedNumber = (value: number, decimals: number): string => {
  const normalized = Math.abs(value) < 10 ** -(decimals + 1) ? 0 : value;
  const sign = normalized > 0 ? '+' : normalized < 0 ? '−' : '';
  return `${sign}${formatNumber(Math.abs(normalized), decimals)}`;
};

const formatPrimaryDelta = (snapshot: ComparisonSnapshot, delta: number): string => {
  if (snapshot.deltaKind === 'duration') {
    const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
    return `${sign}${formatSecondsToTlim(Math.round(Math.abs(delta)))}`;
  }
  if (snapshot.deltaKind === 'gait-speed') return `${formatSignedNumber(delta, 2)} m/s`;
  if (snapshot.deltaKind === 'peak-flow') return `${formatSignedNumber(delta, 1)} L/min`;
  if (snapshot.deltaKind === 'sts-time') return `${formatSignedNumber(delta, 2)} segundos`;
  if (snapshot.deltaKind === 'repetitions') {
    const rounded = Math.round(delta);
    return `${formatSignedNumber(rounded, 0)} ${Math.abs(rounded) === 1 ? 'repetição' : 'repetições'}`;
  }
  return `${formatSignedNumber(delta, 1)} cmH2O`;
};

const compactEnduranceProtocol = (protocol?: RespiratoryEnduranceProtocol | null): string | null => {
  if (!protocol) return null;
  const parts = [getRespiratoryEnduranceModalityLabel(protocol.modality)];

  if (protocol.targetPercent != null) {
    parts.push(`${formatNumber(protocol.targetPercent, 0)}% da ${getRespiratoryEnduranceReferenceLabel(protocol.referenceBasis)}`);
  }
  if (protocol.targetLoad != null) parts.push(`${formatNumber(protocol.targetLoad, 1)} cmH2O`);
  if (protocol.targetMvvPercent != null) parts.push(`${formatNumber(protocol.targetMvvPercent, 0)}% da VVM`);
  if (protocol.targetVentilation != null) parts.push(`${formatNumber(protocol.targetVentilation, 1)} L/min`);
  if (protocol.timeTrialDurationSeconds != null) parts.push(`${formatSecondsToTlim(protocol.timeTrialDurationSeconds)} programados`);
  if (protocol.targetRespiratoryRate != null) parts.push(`${formatNumber(protocol.targetRespiratoryRate, 0)} irpm`);
  if (protocol.pacing) parts.push(protocol.pacing === 'metronome' ? 'metrônomo' : 'ritmo espontâneo');

  return parts.join(' • ');
};

const buildSnapshot = (testKey: AssessmentHistoryComparisonTestKey, payload: unknown): ComparisonSnapshot => {
  if (testKey === 'PIMAX') {
    const summary = summarizePimaxPayload(payload as PimaxResultPayload);
    return {
      pimaxSeries: getPimaxPrimarySeries(payload as PimaxResultPayload),
      primaryValue: summary.max,
      primaryFormatted: summary.max != null ? `${formatNumber(summary.max, 1)} cmH2O` : '—',
      deltaKind: 'pressure',
      lowerIsBetter: false,
      stableTolerance: 0.05,
      percentPredicted: summary.percentPredicted,
      sustained1s: summary.sustained1s,
      referenceKey: null,
      protocol: null,
      protocolLabel: summary.positionLabel,
      chairHeightCm: null,
      usedArms: null,
      walkingAidLabel: null,
      physicalAssistance: null,
      distanceMeters: null,
    };
  }

  if (testKey === 'PEMAX') {
    const summary = summarizePemaxPayload(payload as PemaxResultPayload);
    return {
      primaryValue: summary.max,
      primaryFormatted: summary.max != null ? `${formatNumber(summary.max, 1)} cmH2O` : '—',
      deltaKind: 'pressure',
      lowerIsBetter: false,
      stableTolerance: 0.05,
      percentPredicted: summary.percentPredicted,
      sustained1s: summary.sustained1s,
      referenceKey: null,
      protocol: null,
      protocolLabel: null,
      chairHeightCm: null,
      usedArms: null,
      walkingAidLabel: null,
      physicalAssistance: null,
      distanceMeters: null,
    };
  }

  if (testKey === 'S_INDEX') {
    const summary = summarizeSIndexPayload(payload as SIndexResultPayload);
    return {
      primaryValue: summary.maxPressure,
      primaryFormatted: summary.maxPressure != null ? `${formatNumber(summary.maxPressure, 1)} cmH2O` : '—',
      deltaKind: 'pressure',
      lowerIsBetter: false,
      stableTolerance: 0.05,
      percentPredicted: summary.percentPredicted,
      sustained1s: null,
      referenceKey: summary.referenceEquation,
      protocol: null,
      protocolLabel: summary.referenceLabel,
      chairHeightCm: null,
      usedArms: null,
      walkingAidLabel: null,
      physicalAssistance: null,
      distanceMeters: null,
    };
  }

  if (testKey === 'RESP_ENDURANCE') {
    const endurancePayload = payload as RespiratoryEnduranceResultPayload;
    const summary = summarizeRespiratoryEndurancePayload(endurancePayload);
    return {
      primaryValue: summary.tlimSeconds,
      primaryFormatted: summary.tlimSeconds != null ? formatSecondsToTlim(summary.tlimSeconds) : '—',
      deltaKind: 'duration',
      lowerIsBetter: false,
      stableTolerance: 0.5,
      percentPredicted: null,
      sustained1s: null,
      referenceKey: null,
      protocol: summary.protocol,
      protocolLabel: compactEnduranceProtocol(summary.protocol),
      chairHeightCm: null,
      usedArms: null,
      walkingAidLabel: null,
      physicalAssistance: null,
      distanceMeters: null,
    };
  }

  if (testKey === 'PEAK_FLOW') {
    const summary = summarizePeakFlowPayload(payload as PeakFlowResultPayload);
    const referenceKey =
      summary.referenceMode === 'none' || summary.referenceValue == null
        ? null
        : `${summary.referenceMode}:${summary.referenceSourceKey ?? 'individual'}:${summary.referenceValue.toFixed(1)}`;
    return {
      primaryValue: summary.bestPreFlow,
      primaryFormatted: summary.bestPreFlow != null ? `${formatNumber(summary.bestPreFlow, 1)} L/min` : '—',
      deltaKind: 'peak-flow',
      lowerIsBetter: false,
      stableTolerance: 0.5,
      percentPredicted: summary.referencePercentPre,
      sustained1s: null,
      referenceKey,
      protocol: null,
      protocolLabel: summary.zone === 'unclassified' ? null : summary.zoneLabel,
      chairHeightCm: null,
      usedArms: null,
      walkingAidLabel: null,
      physicalAssistance: null,
      distanceMeters: null,
    };
  }

  if (testKey === '4MGS') {
    const gaitPayload = payload as GaitSpeedResultPayload;
    const summary = summarizeGaitSpeedResult(gaitPayload);
    const distanceMeters = gaitPayload?.protocol?.distanceMeters ?? 4;
    return {
      primaryValue: summary.bestSpeedMs,
      primaryFormatted: summary.bestSpeedMs != null ? `${formatNumber(summary.bestSpeedMs, 2)} m/s` : '—',
      deltaKind: 'gait-speed',
      lowerIsBetter: false,
      stableTolerance: 0.005,
      percentPredicted: summary.referenceComparison?.percentPredicted ?? null,
      sustained1s: null,
      referenceKey: summary.referenceComparison?.source ?? null,
      protocol: null,
      protocolLabel: `${formatNumber(distanceMeters, 0)} m • ${summary.walkingAidLabel}`,
      chairHeightCm: null,
      usedArms: null,
      walkingAidLabel: summary.walkingAidLabel,
      physicalAssistance: summary.physicalAssistance,
      distanceMeters,
    };
  }

  const summary = summarizeStsResult(testKey as StsTestKey, payload);
  return {
    primaryValue: summary.best,
    primaryFormatted:
      summary.best == null
        ? '—'
        : testKey === '5xSTS'
          ? `${formatNumber(summary.best, 2)} segundos`
          : `${formatNumber(Math.round(summary.best), 0)} ${Math.round(summary.best) === 1 ? 'repetição' : 'repetições'}`,
    deltaKind: testKey === '5xSTS' ? 'sts-time' : 'repetitions',
    lowerIsBetter: summary.lowerIsBetter,
    stableTolerance: testKey === '5xSTS' ? 0.005 : 0.5,
    percentPredicted: summary.referenceComparison?.percentPredicted ?? null,
    sustained1s: null,
    referenceKey: summary.referenceComparison?.source ?? null,
    protocol: null,
    protocolLabel: summary.chairHeightCm != null ? `Cadeira de ${formatNumber(summary.chairHeightCm, 0)} cm` : null,
    chairHeightCm: summary.chairHeightCm,
    usedArms: summary.usedArms,
    walkingAidLabel: null,
    physicalAssistance: null,
    distanceMeters: null,
  };
};

const optionalNumberMatches = (left?: number | null, right?: number | null, tolerance = 0.1): boolean => {
  if (left == null && right == null) return true;
  if (left == null || right == null) return false;
  return Math.abs(left - right) <= tolerance;
};

const hasDocumentedEnduranceDose = (protocol: RespiratoryEnduranceProtocol): boolean => {
  if (protocol.modality === 'threshold') return protocol.targetLoad != null || protocol.targetPercent != null;
  if (protocol.modality === 'hyperpnea') return protocol.targetVentilation != null || protocol.targetMvvPercent != null;
  if (protocol.modality === 'time_trial') return protocol.timeTrialDurationSeconds != null;
  return protocol.targetLoad != null || protocol.targetPercent != null || protocol.timeTrialDurationSeconds != null;
};

const enduranceProtocolsMatch = (current?: RespiratoryEnduranceProtocol | null, previous?: RespiratoryEnduranceProtocol | null): boolean => {
  if (!current || !previous || !hasDocumentedEnduranceDose(current) || !hasDocumentedEnduranceDose(previous)) return false;
  if (!current.pacing || !previous.pacing || current.modality !== previous.modality || current.pacing !== previous.pacing) return false;
  if (current.pacing === 'metronome' && (current.targetRespiratoryRate == null || previous.targetRespiratoryRate == null)) return false;
  if (!optionalNumberMatches(current.targetRespiratoryRate, previous.targetRespiratoryRate, 0.5)) return false;

  if (current.modality === 'threshold') {
    return (
      current.thresholdProtocol === previous.thresholdProtocol &&
      current.referenceBasis === previous.referenceBasis &&
      optionalNumberMatches(current.targetPercent, previous.targetPercent, 0.5) &&
      optionalNumberMatches(current.targetLoad, previous.targetLoad) &&
      optionalNumberMatches(current.startingPercent, previous.startingPercent, 0.5) &&
      optionalNumberMatches(current.incrementPercent, previous.incrementPercent, 0.5) &&
      optionalNumberMatches(current.stageDurationSeconds, previous.stageDurationSeconds, 1)
    );
  }

  if (current.modality === 'hyperpnea') {
    return (
      optionalNumberMatches(current.targetMvvPercent, previous.targetMvvPercent, 0.5) &&
      optionalNumberMatches(current.targetVentilation, previous.targetVentilation) &&
      optionalNumberMatches(current.targetTidalVolume, previous.targetTidalVolume) &&
      current.isocapnia === previous.isocapnia
    );
  }

  return (
    optionalNumberMatches(current.timeTrialDurationSeconds, previous.timeTrialDurationSeconds, 1) &&
    optionalNumberMatches(current.targetLoad, previous.targetLoad) &&
    optionalNumberMatches(current.targetPercent, previous.targetPercent, 0.5)
  );
};

const resolveCompatibility = (testKey: AssessmentHistoryComparisonTestKey, current: ComparisonSnapshot, previous: ComparisonSnapshot): Compatibility => {
  if (testKey === 'PIMAX') return current.pimaxSeries && previous.pimaxSeries && pimaxSeriesAreComparable(current.pimaxSeries, previous.pimaxSeries)
    ? { comparable: true } : { comparable: false, note: 'Posição ou técnica diferente/não informada; sem comparação numérica.' };
  if (testKey === 'RESP_ENDURANCE') {
    return enduranceProtocolsMatch(current.protocol, previous.protocol)
      ? { comparable: true }
      : {
          comparable: false,
          note: 'Protocolo diferente ou insuficientemente documentado',
        };
  }

  if (testKey === 'S_INDEX' && current.referenceKey !== previous.referenceKey) {
    return {
      comparable: true,
      note: 'Equação de referência diferente; compare apenas o valor absoluto',
    };
  }

  if (testKey === 'PEAK_FLOW' && current.referenceKey !== previous.referenceKey) {
    return {
      comparable: true,
      note: 'Referências diferentes; compare apenas o valor absoluto',
    };
  }

  if (testKey === '4MGS') {
    const comparable =
      current.physicalAssistance === false &&
      previous.physicalAssistance === false &&
      current.walkingAidLabel != null &&
      current.walkingAidLabel === previous.walkingAidLabel &&
      current.distanceMeters != null &&
      previous.distanceMeters != null &&
      Math.abs(current.distanceMeters - previous.distanceMeters) <= 0.1;
    return comparable
      ? { comparable: true }
      : {
          comparable: false,
          note: 'Auxílio, assistência física ou percurso diferentes',
        };
  }

  if (testKey === '5xSTS' || testKey === 'STS_30S' || testKey === 'STS_1MIN') {
    const sameChair = current.chairHeightCm != null && previous.chairHeightCm != null && Math.abs(current.chairHeightCm - previous.chairHeightCm) <= 0.5;
    const sameArmUse = testKey !== '5xSTS' || (current.usedArms != null && previous.usedArms != null && current.usedArms === previous.usedArms);
    return sameChair && sameArmUse ? { comparable: true } : { comparable: false, note: 'Protocolo diferente ou incompleto' };
  }

  return { comparable: true };
};

const resolveDirection = (current: ComparisonSnapshot, delta: number, compatibility: Compatibility): { label: string; className: string } => {
  if (!compatibility.comparable)
    return {
      label: 'Protocolos diferentes',
      className: 'bg-slate-100 text-slate-600',
    };
  if (Math.abs(delta) <= current.stableTolerance) return { label: 'Estável', className: 'bg-slate-100 text-slate-600' };
  const favorable = current.lowerIsBetter ? delta < 0 : delta > 0;
  return favorable
    ? {
        label: 'Direção favorável',
        className: 'bg-emerald-50 text-emerald-700',
      }
    : { label: 'Direção desfavorável', className: 'bg-rose-50 text-rose-700' };
};

const getSecondaryLines = (snapshot: ComparisonSnapshot): string[] => {
  const lines: string[] = [];
  if (snapshot.percentPredicted != null) {
    lines.push(
      snapshot.deltaKind === 'peak-flow'
        ? `${formatNumber(snapshot.percentPredicted, 1)}% da referência`
        : `${formatNumber(snapshot.percentPredicted, 1)}% do predito`,
    );
  }
  if (snapshot.sustained1s != null) lines.push(`Sustentada: ${formatNumber(snapshot.sustained1s, 1)} cmH2O`);
  if (snapshot.protocolLabel) lines.push(snapshot.protocolLabel);
  return lines;
};

export default function AssessmentHistoryComparisonCard({
  testKey,
  currentPayload,
  currentAssessmentDate,
  history = [],
}: AssessmentHistoryComparisonCardProps) {
  const current = buildSnapshot(testKey, currentPayload);
  if (current.primaryValue == null) return null;

  const rows = history
    .slice(0, 5)
    .map((entry) => ({
      entry,
      snapshot: buildSnapshot(testKey, testKey === 'PIMAX' && current.pimaxSeries
        ? getPimaxPositionSeries(entry.payload as PimaxResultPayload).find((item) => pimaxSeriesAreComparable(current.pimaxSeries!, item))?.result ?? entry.payload
        : entry.payload),
    }))
    .filter((item) => item.snapshot.primaryValue != null);

  if (!rows.length) return null;

  return (
    <section className="overflow-hidden rounded-[24px] border border-slate-100 bg-white shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Comparação com resultados anteriores</div>
          <p className="mt-1 text-xs text-slate-500">Diferença calculada como resultado atual menos resultado anterior.</p>
        </div>
        <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700">
          Atual em {formatAssessmentDate(currentAssessmentDate)} • {current.primaryFormatted}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[760px] w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
              <th className="px-5 py-3">Data</th>
              <th className="px-5 py-3">Resultado anterior</th>
              <th className="px-5 py-3">Diferença para o atual</th>
              <th className="px-5 py-3">Leitura</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(({ entry, snapshot }) => {
              const delta = current.primaryValue! - snapshot.primaryValue!;
              const relativeDelta = snapshot.primaryValue !== 0 ? (delta / Math.abs(snapshot.primaryValue!)) * 100 : null;
              const compatibility = resolveCompatibility(testKey, current, snapshot);
              const direction = resolveDirection(current, delta, compatibility);
              const canCompareReference = current.referenceKey === snapshot.referenceKey;
              const percentPredictedDelta =
                compatibility.comparable && canCompareReference && current.percentPredicted != null && snapshot.percentPredicted != null
                  ? current.percentPredicted - snapshot.percentPredicted
                  : null;
              const sustainedDelta = compatibility.comparable && current.sustained1s != null && snapshot.sustained1s != null ? current.sustained1s - snapshot.sustained1s : null;

              return (
                <tr key={entry.id} className="align-top text-slate-700">
                  <td className="whitespace-nowrap px-5 py-4 font-medium text-slate-900">{formatAssessmentDate(entry.assessmentDate)}</td>
                  <td className="px-5 py-4">
                    <div className="font-bold text-slate-900">{snapshot.primaryFormatted}</div>
                    {getSecondaryLines(snapshot).map((line) => (
                      <div key={line} className="mt-1 text-xs text-slate-500">
                        {line}
                      </div>
                    ))}
                  </td>
                  <td className="px-5 py-4">
                    <div className="font-bold text-slate-900">
                      {testKey === 'PIMAX' && !compatibility.comparable ? 'Comparação indisponível' : <>{formatPrimaryDelta(current, delta)}{relativeDelta != null ? ` (${formatSignedNumber(relativeDelta, 1)}%)` : ''}</>}
                    </div>
                    {percentPredictedDelta != null ? (
                      <div className="mt-1 text-xs text-slate-500">
                        {formatSignedNumber(percentPredictedDelta, 1)} p.p. {current.deltaKind === 'peak-flow' ? 'da referência' : 'do predito'}
                      </div>
                    ) : null}
                    {sustainedDelta != null ? (
                      <div className="mt-1 text-xs text-slate-500">Sustentada: {formatSignedNumber(sustainedDelta, 1)} cmH2O</div>
                    ) : null}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${direction.className}`}>{direction.label}</span>
                    {compatibility.note ? <div className="mt-2 max-w-xs text-xs leading-relaxed text-slate-500">{compatibility.note}</div> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
